import { loadBitmap, type AssetCatalog } from './assets';
import { BombArtLibrary } from './bomb-art';
import { CraterBrinks } from './crater-brink';
import type { TerrainMask } from './mask';
import { ParticleArtLibrary } from './particle-art';
import { WingArtLibrary } from './wing-art';

/**
 * As bibliotecas de arte da partida, uma vez por catálogo de assets. A tela de confronto as aquece e o renderer da partida as reaproveita:
 * o que já baixou e foi decodificado não baixa de novo ao abrir a partida. Elas guardam só imagens e texturas, nunca o estado de um renderer.
 */
export interface SharedArt {
  bombArt: BombArtLibrary;
  wingArt: WingArtLibrary;
  particleArt: ParticleArtLibrary;
  brinks: CraterBrinks;
}

const registry = new WeakMap<AssetCatalog, SharedArt>();

export function sharedArt(assets: AssetCatalog): SharedArt {
  let art = registry.get(assets);
  if (!art) {
    art = { bombArt: new BombArtLibrary(assets), wingArt: new WingArtLibrary(assets), particleArt: new ParticleArtLibrary(assets), brinks: new CraterBrinks(assets) };
    registry.set(assets, art);
  }
  return art;
}

/** Bitmaps já pedidos por URL. Ninguém os fecha: quem usa só cria texturas a partir deles. Falha de rede sai do cache para tentar de novo. */
const bitmaps = new Map<string, Promise<ImageBitmap | null>>();

export function cachedBitmap(url: string): Promise<ImageBitmap | null> {
  let cached = bitmaps.get(url);
  if (!cached) {
    cached = loadBitmap(url);
    bitmaps.set(url, cached);
    void cached.then((bitmap) => {
      if (bitmap === null) bitmaps.delete(url);
    });
  }
  return cached;
}

/** Máscara da cratera da bomba: a do manifest de artes ou, se ele não a traz, a que o site serve em `craterMaskBase`. */
export async function loadCraterMask(assets: AssetCatalog, ballId: number, craterMaskBase?: string): Promise<TerrainMask | null> {
  try {
    if (assets.hasCrater(ballId)) return await assets.mask(assets.craterMaskPath(ballId));
    return craterMaskBase ? await assets.maskAt(`${craterMaskBase.replace(/\/+$/, '')}/${ballId}.mask`) : null;
  } catch {
    return null;
  }
}
